import {useCallback, useMemo} from "react";
import {Card} from "../../../../../components/card/card.jsx";
import SentryEchart from "../../../../../components/echarts/sentry-echart.jsx";
import ExpressiveReportCard from "../../../../../components/reports/expressive-alert-card/expressive-report-card.jsx";
import useGraphViewportWidth from "../../../../../hooks/use-graph-viewport-width.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {classNameUtils} from "../../../../../utils/class-name-utils.js";
import {fluidPx} from "../../../../../utils/responsive-graph.js";
import {unavailableReasonLabels} from "../../maintenances/utils/maintenances-utils.jsx";

const customStyle = classNameUtils.cardVariants("customMenu");

const UNAVAILABLE_REASON_COLORS = {
  MAINTENANCE: "#20609D",
  EXPERTISE: "#50A6F9",
  SEIZURE: "#64C5C5",
  LOSS: "#E9D89D"
};

const EMPTY_CHART_COLOR = "#E8EEF5";

const StockUnavailabilityGraph = ({data}) => {
  const vw = useGraphViewportWidth();
  const navigate = navigateHook();

  const stockAlertTotal = Number(data?.stockAlertsCount?.alert ?? data?.stockAlertsCount?.ALERT ?? 0);
  const unavailableMaintenancesCount = data?.unavailableMaintenancesCount ?? {};
  const unavailableItems = useMemo(() => (
    Object.entries(unavailableReasonLabels).map(([key, label]) => ({
      key,
      label,
      value: Number(unavailableMaintenancesCount[key] ?? 0)
    }))
  ), [unavailableMaintenancesCount]);

  const createSemiGaugeOption = useCallback((seriesName, centerText, items) => {
    const legendFs = fluidPx(11, 18, vw);
    const labelFs = fluidPx(11, 18, vw);
    const chartItems = items.some(item => item.value > 0)
      ? items
      : [{name: "Sem registros", value: 1, tooltipValue: 0, itemStyle: {color: EMPTY_CHART_COLOR}}];

    return {
      tooltip: {
        trigger: "item",
        extraCssText: "max-width: 320px; white-space: normal;",
        formatter: (params) => (
          params.marker + " " + params.name + ": <b>" + (params.data.tooltipValue ?? params.value) + "</b>"
        )
      },
      title: {
        text: centerText,
        left: "60%",
        top: "5%",
        textStyle: {fontSize: legendFs, color: "#676a6c", fontWeight: "bold"}
      },
      legend: {
        orient: "vertical",
        left: "60%",
        top: "15%",
        icon: "circle",
        itemGap: 8,
        itemWidth: 8,
        itemHeight: 8,
        textStyle: {fontSize: legendFs, color: "#676a6c"}
      },
      series: [
        {
          name: seriesName,
          type: "pie",
          radius: ["60%", "105%"],
          center: ["34%", "70%"],
          startAngle: 180,
          endAngle: 360,
          padAngle: 4,
          itemStyle: {borderRadius: 8},
          emphasis: {
            focus: "self",
            itemStyle: {
              shadowBlur: 8,
              shadowColor: "rgba(0, 0, 0, 0.15)"
            }
          },
          blur: {
            itemStyle: {opacity: 0.25}
          },
          label: {
            show: true,
            fontSize: labelFs,
            color: "#063A6C",
            formatter: ({data: item, value}) => item.tooltipValue ?? value
          },
          labelLine: {length: 6, length2: 6},
          data: chartItems
        }
      ]
    };
  }, [vw]);

  const unavailableOption = useMemo(() => (
    createSemiGaugeOption("Indisponíveis", "Motivo:", unavailableItems.map(item => ({
      name: item.label,
      value: item.value,
      itemStyle: {color: UNAVAILABLE_REASON_COLORS[item.key]}
    })))
  ), [createSemiGaugeOption, unavailableItems]);

  return (
    <Card className="col-span-3 h-full min-h-0 w-full flex flex-col">
      <Card.Header className={customStyle.header}>
        <Card.Title className={customStyle.title}>Alertas e indisponibilidades</Card.Title>
      </Card.Header>
      <Card.Body className={customStyle.body}>
        <div className="flex h-full min-h-0 flex-col justify-between">
          <div className="w-full px-15">
            <ExpressiveReportCard alert="Alerta de estoque"
                                  noAlert="Nenhum alerta de estoque"
                                  alertMessage="Todos os materiais com quantidade suficiente"
                                  noAlertMessage={`${stockAlertTotal} materiais com  estoque mínimo atigindo`}
                                  value={stockAlertTotal}
                                  onOpen={() => navigate("/materials/alerts", true)}
            />
          </div>
          <SentryEchart
            className="min-h-0"
            height="65%"
            option={unavailableOption}
            title="Indisponíveis"
            titleClass="text-[#676a6c]! mt-3! mb-5!"
          />
        </div>
      </Card.Body>
    </Card>
  )
}

export default StockUnavailabilityGraph;
